import { KanbanBoard, KanbanColumn, KanbanCard, KanbanUser, KanbanRow, KanbanAttachment, ChecklistItem, CreateCardRequest, UpdateCardRequest, MoveCardRequest, CreateRowRequest, UpdateRowRequest, BoardInfo } from '../types/kanban';
import config from '../components/sample/lib/config';

interface CreateBoardRequest {
  boardId: string;
  title: string;
  teamId?: string;
  channelId?: string;
  chatId?: string;
  members: KanbanUser[];
  createdBy: KanbanUser;
}

/**
 * KanbanServiceV2 - Uses normalized data model with separate API endpoints
 * for cards, comments, attachments, and checklist items.
 *
 * Key differences from V1:
 * - Individual entity operations call specific endpoints
 * - No need to reload/save entire board for each operation
 * - Better concurrency handling - operations don't overwrite each other
 */
export class KanbanServiceV2 {
  private static instance: KanbanServiceV2;
  private apiEndpoint: string;
  private localCache: Map<string, { board: KanbanBoard; lastModified: string }> = new Map();

  private constructor() {
    const endpoint = config.apiEndpoint || '';
    this.apiEndpoint = endpoint || '/api';
  }

  public static getInstance(): KanbanServiceV2 {
    if (!KanbanServiceV2.instance) {
      KanbanServiceV2.instance = new KanbanServiceV2();
    }
    return KanbanServiceV2.instance;
  }

  private generateId(): string {
    return Math.random().toString(36).substring(2) + Date.now().toString(36);
  }

  private createDefaultColumns(): KanbanColumn[] {
    return [
      { id: this.generateId(), title: 'To Do', order: 0, color: '#E3F2FD' },
      { id: this.generateId(), title: 'In Progress', order: 1, color: '#FFF3E0' },
      { id: this.generateId(), title: 'Done', order: 2, color: '#E8F5E8' },
    ];
  }

  private createDefaultRows(): KanbanRow[] {
    return [{ id: this.generateId(), name: 'General', order: 0 }];
  }

  /**
   * Parse date strings back to Date objects for the board and all nested entities
   */
  private parseDates(board: any): KanbanBoard {
    board.createdAt = new Date(board.createdAt);
    board.updatedAt = new Date(board.updatedAt);

    if (board.cards) {
      board.cards.forEach((card: any) => {
        card.createdAt = new Date(card.createdAt);
        card.updatedAt = new Date(card.updatedAt);
        if (card.dueDate) card.dueDate = new Date(card.dueDate);
        if (card.startDate) card.startDate = new Date(card.startDate);
        if (card.endDate) card.endDate = new Date(card.endDate);
        if (card.archivedAt) card.archivedAt = new Date(card.archivedAt);

        // Parse comment dates
        if (card.comments) {
          card.comments.forEach((comment: any) => {
            comment.createdAt = new Date(comment.createdAt);
            if (comment.updatedAt) comment.updatedAt = new Date(comment.updatedAt);
          });
        }

        // Parse attachment dates
        if (card.attachments) {
          card.attachments.forEach((attachment: any) => {
            attachment.uploadedAt = new Date(attachment.uploadedAt);
          });
        }

        // Parse checklist item dates
        if (card.checklist) {
          card.checklist.forEach((item: any) => {
            item.createdAt = new Date(item.createdAt);
            if (item.updatedAt) item.updatedAt = new Date(item.updatedAt);
          });
        }
      });
    }

    return board as KanbanBoard;
  }

  /**
   * Get a board with all its cards, comments, attachments, and checklist items (hydrated)
   */
  public async getBoard(boardId: string): Promise<KanbanBoard | null> {
    const response = await fetch(`${this.apiEndpoint}/v2/boards/${boardId}/full`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      throw new Error(`Failed to get board: ${response.statusText}`);
    }

    const board = await response.json();
    const rawUpdatedAt = board.updatedAt; // Preserve string before parseDates mutates it
    const parsedBoard = this.parseDates(board);

    // Update cache
    this.localCache.set(boardId, {
      board: parsedBoard,
      lastModified: rawUpdatedAt,
    });

    return parsedBoard;
  }

  /**
   * Sync board - only fetches if modified since last sync
   */
  public async syncBoard(boardId: string): Promise<KanbanBoard | null> {
    try {
      const cached = this.localCache.get(boardId);
      const lastModified = cached?.lastModified;

      const url = lastModified
        ? `${this.apiEndpoint}/v2/boards/${boardId}/sync?lastModified=${encodeURIComponent(lastModified)}`
        : `${this.apiEndpoint}/v2/boards/${boardId}/sync`;

      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.status === 404) {
        return null;
      }

      // 304 Not Modified - return cached version
      if (response.status === 304) {
        return cached?.board || null;
      }

      if (!response.ok) {
        throw new Error(`Failed to sync board: ${response.statusText}`);
      }

      const data = await response.json();
      const rawUpdatedAt = data.updatedAt; // Preserve string before parseDates mutates it
      const parsedBoard = this.parseDates(data);

      // Update cache
      this.localCache.set(boardId, {
        board: parsedBoard,
        lastModified: rawUpdatedAt,
      });

      return parsedBoard;
    } catch (error) {
      console.error('Error syncing board:', error);
      return this.localCache.get(boardId)?.board || null;
    }
  }

  /**
   * Create a new board
   */
  public async createBoard(request: CreateBoardRequest): Promise<KanbanBoard> {
    const response = await fetch(`${this.apiEndpoint}/v2/boards`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: request.boardId,
        title: request.title,
        columns: this.createDefaultColumns(),
        rows: this.createDefaultRows(),
        members: request.members,
        teamId: request.teamId,
        channelId: request.channelId,
        chatId: request.chatId,
        createdBy: request.createdBy,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to create board: ${response.statusText}`);
    }

    const board = await response.json();

    // The created board won't have cards yet, add empty array
    const fullBoard = { ...board, cards: [] };
    const parsedBoard = this.parseDates(fullBoard);

    // Update cache
    this.localCache.set(request.boardId, {
      board: parsedBoard,
      lastModified: board.updatedAt,
    });

    return parsedBoard;
  }

  /**
   * Update board metadata (title, columns, rows, members)
   */
  public async updateBoard(boardId: string, updates: Partial<KanbanBoard>): Promise<KanbanBoard> {
    const response = await fetch(`${this.apiEndpoint}/v2/boards/${boardId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        title: updates.title,
        columns: updates.columns,
        rows: updates.rows,
        members: updates.members,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to update board: ${response.statusText}`);
    }

    // Refetch full board to get updated state with cards
    return await this.getBoard(boardId) as KanbanBoard;
  }

  /**
   * Create a new card
   */
  public async createCard(boardId: string, request: CreateCardRequest): Promise<KanbanBoard> {
    const response = await fetch(`${this.apiEndpoint}/v2/boards/${boardId}/cards`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        title: request.title,
        description: request.description || '',
        assignedTo: request.assignedTo || [],
        priority: request.priority || 'medium',
        tags: request.tags || [],
        dueDate: request.dueDate,
        startDate: request.startDate,
        endDate: request.endDate,
        color: request.color,
        columnId: request.columnId,
        rowId: request.rowId,
        createdBy: request.createdBy,
        lastModifiedBy: request.lastModifiedBy,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to create card: ${response.statusText}`);
    }

    // Refetch board to get updated state
    return await this.getBoard(boardId) as KanbanBoard;
  }

  /**
   * Update a card
   */
  public async updateCard(boardId: string, request: UpdateCardRequest): Promise<KanbanBoard> {
    const response = await fetch(`${this.apiEndpoint}/v2/boards/${boardId}/cards/${request.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        title: request.title,
        description: request.description,
        assignedTo: request.assignedTo,
        priority: request.priority,
        tags: request.tags,
        dueDate: request.dueDate,
        startDate: request.startDate,
        endDate: request.endDate,
        color: request.color,
        columnId: request.columnId,
        rowId: request.rowId,
        archived: request.archived,
        updatedBy: request.updatedBy,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to update card: ${response.statusText}`);
    }

    // Refetch board to get updated state
    return await this.getBoard(boardId) as KanbanBoard;
  }

  /**
   * Move a card to a different column/row
   */
  public async moveCard(boardId: string, request: MoveCardRequest): Promise<KanbanBoard> {
    const response = await fetch(`${this.apiEndpoint}/v2/boards/${boardId}/cards/${request.cardId}/move`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        targetColumnId: request.targetColumnId,
        targetRowId: request.targetRowId,
        targetOrder: request.targetIndex ?? 0,
        movedBy: request.movedBy,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to move card: ${response.statusText}`);
    }

    // Refetch board to get updated state
    return await this.getBoard(boardId) as KanbanBoard;
  }

  /**
   * Delete a card (cascade deletes comments, attachments, checklist items)
   */
  public async deleteCard(boardId: string, cardId: string): Promise<KanbanBoard> {
    const response = await fetch(`${this.apiEndpoint}/v2/boards/${boardId}/cards/${cardId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok && response.status !== 404) {
      throw new Error(`Failed to delete card: ${response.statusText}`);
    }

    // Refetch board to get updated state
    return await this.getBoard(boardId) as KanbanBoard;
  }

  /**
   * Delete multiple cards at once
   */
  public async deleteCards(boardId: string, cardIds: string[]): Promise<KanbanBoard> {
    // Delete all cards in parallel
    await Promise.all(
      cardIds.map(cardId =>
        fetch(`${this.apiEndpoint}/v2/boards/${boardId}/cards/${cardId}`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
        })
      )
    );

    return await this.getBoard(boardId) as KanbanBoard;
  }

  /**
   * Archive a card
   */
  public async archiveCard(boardId: string, cardId: string, updatedBy?: string): Promise<KanbanBoard> {
    const response = await fetch(`${this.apiEndpoint}/v2/boards/${boardId}/cards/${cardId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ archived: true, updatedBy }),
    });

    if (!response.ok) {
      throw new Error(`Failed to archive card: ${response.statusText}`);
    }

    return await this.getBoard(boardId) as KanbanBoard;
  }

  /**
   * Archive multiple cards at once
   */
  public async archiveCards(boardId: string, cardIds: string[], updatedBy?: string): Promise<KanbanBoard> {
    // Archive all cards in parallel
    await Promise.all(
      cardIds.map(cardId =>
        fetch(`${this.apiEndpoint}/v2/boards/${boardId}/cards/${cardId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ archived: true, updatedBy }),
        })
      )
    );

    return await this.getBoard(boardId) as KanbanBoard;
  }

  /**
   * Unarchive a card
   */
  public async unarchiveCard(boardId: string, cardId: string, updatedBy?: string): Promise<KanbanBoard> {
    const response = await fetch(`${this.apiEndpoint}/v2/boards/${boardId}/cards/${cardId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ archived: false, updatedBy }),
    });

    if (!response.ok) {
      throw new Error(`Failed to unarchive card: ${response.statusText}`);
    }

    return await this.getBoard(boardId) as KanbanBoard;
  }

  /**
   * Get archived cards for a board
   */
  public async getArchivedCards(boardId: string): Promise<KanbanCard[]> {
    const board = await this.getBoard(boardId);
    if (!board) return [];
    return board.cards.filter(card => card.archived === true);
  }

  /**
   * Search cards
   */
  public async searchCards(boardId: string, query: string): Promise<KanbanCard[]> {
    const board = await this.getBoard(boardId);
    if (!board) return [];

    const lowercaseQuery = query.toLowerCase();

    return board.cards.filter(card =>
      card.title.toLowerCase().includes(lowercaseQuery) ||
      card.description?.toLowerCase().includes(lowercaseQuery) ||
      card.tags?.some(tag => tag.toLowerCase().includes(lowercaseQuery)) ||
      card.assignedTo?.some(user => user.displayName.toLowerCase().includes(lowercaseQuery))
    );
  }

  /**
   * Get cards by assignee
   */
  public async getCardsByAssignee(boardId: string, userId: string): Promise<KanbanCard[]> {
    const board = await this.getBoard(boardId);
    if (!board) return [];
    return board.cards.filter(card =>
      card.assignedTo?.some(user => user.id === userId)
    );
  }

  // ============================================
  // Row Operations (still part of board metadata)
  // ============================================

  public async createRow(boardId: string, request: CreateRowRequest): Promise<KanbanBoard> {
    const board = await this.getBoard(boardId);
    if (!board) throw new Error('Board not found');

    const newRow: KanbanRow = {
      id: this.generateId(),
      name: request.name,
      order: board.rows.length,
    };

    return await this.updateBoard(boardId, {
      rows: [...board.rows, newRow],
    });
  }

  public async updateRow(boardId: string, request: UpdateRowRequest): Promise<KanbanBoard> {
    const board = await this.getBoard(boardId);
    if (!board) throw new Error('Board not found');

    const updatedRows = board.rows.map(row =>
      row.id === request.rowId ? { ...row, name: request.name } : row
    );

    return await this.updateBoard(boardId, { rows: updatedRows });
  }

  public async deleteRow(boardId: string, rowId: string): Promise<KanbanBoard> {
    const board = await this.getBoard(boardId);
    if (!board) throw new Error('Board not found');

    // Check for cards in row
    const cardsInRow = board.cards.filter(card => card.rowId === rowId);
    if (cardsInRow.length > 0) {
      throw new Error('Cannot delete row with tasks. Please move or delete all tasks first.');
    }

    const updatedRows = board.rows.filter(row => row.id !== rowId);
    return await this.updateBoard(boardId, { rows: updatedRows });
  }

  // ============================================
  // Comment Operations (individual API calls)
  // ============================================

  public async addComment(boardId: string, cardId: string, text: string, author: KanbanUser): Promise<KanbanBoard> {
    const response = await fetch(`${this.apiEndpoint}/v2/boards/${boardId}/cards/${cardId}/comments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        text,
        authorId: author.id,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to add comment: ${response.statusText}`);
    }

    return await this.getBoard(boardId) as KanbanBoard;
  }

  public async updateComment(boardId: string, cardId: string, commentId: string, text: string): Promise<KanbanBoard> {
    const response = await fetch(`${this.apiEndpoint}/v2/boards/${boardId}/cards/${cardId}/comments/${commentId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text }),
    });

    if (!response.ok) {
      throw new Error(`Failed to update comment: ${response.statusText}`);
    }

    return await this.getBoard(boardId) as KanbanBoard;
  }

  public async deleteComment(boardId: string, cardId: string, commentId: string): Promise<KanbanBoard> {
    const response = await fetch(`${this.apiEndpoint}/v2/boards/${boardId}/cards/${cardId}/comments/${commentId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok && response.status !== 404) {
      throw new Error(`Failed to delete comment: ${response.statusText}`);
    }

    return await this.getBoard(boardId) as KanbanBoard;
  }

  // ============================================
  // Attachment Operations (individual API calls)
  // ============================================

  public async addAttachment(boardId: string, cardId: string, file: File, uploadedBy: KanbanUser): Promise<KanbanBoard> {
    // Check file size limit (25MB)
    const maxSize = 25 * 1024 * 1024;
    if (file.size > maxSize) {
      throw new Error(`File size exceeds 25MB limit. File is ${(file.size / 1024 / 1024).toFixed(2)}MB.`);
    }

    // Convert file to base64
    const base64Data = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    const response = await fetch(`${this.apiEndpoint}/v2/boards/${boardId}/cards/${cardId}/attachments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        fileName: file.name,
        fileData: base64Data,
        fileType: file.type,
        uploadedBy: uploadedBy.id,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to upload attachment: ${response.statusText}`);
    }

    return await this.getBoard(boardId) as KanbanBoard;
  }

  public async removeAttachment(boardId: string, cardId: string, attachmentId: string): Promise<KanbanBoard> {
    const response = await fetch(`${this.apiEndpoint}/v2/boards/${boardId}/cards/${cardId}/attachments/${attachmentId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok && response.status !== 404) {
      throw new Error(`Failed to delete attachment: ${response.statusText}`);
    }

    return await this.getBoard(boardId) as KanbanBoard;
  }

  public downloadAttachment(attachment: KanbanAttachment): void {
    const a = document.createElement('a');
    a.href = attachment.blobUrl;
    a.download = attachment.name;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  // ============================================
  // Checklist Operations (individual API calls)
  // ============================================

  public async addChecklistItem(boardId: string, cardId: string, text: string): Promise<KanbanBoard> {
    const response = await fetch(`${this.apiEndpoint}/v2/boards/${boardId}/cards/${cardId}/checklist`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text }),
    });

    if (!response.ok) {
      throw new Error(`Failed to add checklist item: ${response.statusText}`);
    }

    return await this.getBoard(boardId) as KanbanBoard;
  }

  public async updateChecklistItem(boardId: string, cardId: string, itemId: string, text: string): Promise<KanbanBoard> {
    const response = await fetch(`${this.apiEndpoint}/v2/boards/${boardId}/cards/${cardId}/checklist/${itemId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text }),
    });

    if (!response.ok) {
      throw new Error(`Failed to update checklist item: ${response.statusText}`);
    }

    return await this.getBoard(boardId) as KanbanBoard;
  }

  public async toggleChecklistItem(boardId: string, cardId: string, itemId: string): Promise<KanbanBoard> {
    const response = await fetch(`${this.apiEndpoint}/v2/boards/${boardId}/cards/${cardId}/checklist/${itemId}/toggle`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      throw new Error(`Failed to toggle checklist item: ${response.statusText}`);
    }

    return await this.getBoard(boardId) as KanbanBoard;
  }

  public async deleteChecklistItem(boardId: string, cardId: string, itemId: string): Promise<KanbanBoard> {
    const response = await fetch(`${this.apiEndpoint}/v2/boards/${boardId}/cards/${cardId}/checklist/${itemId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok && response.status !== 404) {
      throw new Error(`Failed to delete checklist item: ${response.statusText}`);
    }

    return await this.getBoard(boardId) as KanbanBoard;
  }

  // ============================================
  // Board Management
  // ============================================

  /**
   * List all boards for a given context (by teamId/channelId/chatId)
   */
  public async listBoards(contextPrefix: string): Promise<BoardInfo[]> {
    // Use prefix filtering to match board IDs that start with the context prefix
    const queryParams = `prefix=${encodeURIComponent(contextPrefix)}`;

    const response = await fetch(`${this.apiEndpoint}/v2/boards?${queryParams}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      throw new Error(`Failed to list boards: ${response.statusText}`);
    }

    const boards = await response.json();
    return boards.map((b: any) => ({
      id: b.id || b.boardId,
      title: b.title,
      createdAt: new Date(b.createdAt),
      updatedAt: new Date(b.updatedAt),
    }));
  }

  /**
   * Delete a board and all its data
   */
  public async deleteBoard(boardId: string): Promise<void> {
    const response = await fetch(`${this.apiEndpoint}/v2/boards/${boardId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok && response.status !== 404) {
      throw new Error(`Failed to delete board: ${response.statusText}`);
    }

    this.localCache.delete(boardId);
  }

  /**
   * Rename a board
   */
  public async renameBoard(boardId: string, newTitle: string): Promise<KanbanBoard> {
    return await this.updateBoard(boardId, { title: newTitle });
  }

  /**
   * Generate a unique board ID for a new board in a context
   */
  public generateBoardId(contextPrefix: string): string {
    return `${contextPrefix}-${this.generateId()}`;
  }
}
